import React, { useMemo, useState, useContext } from 'react';
import styled from 'styled-components';
import { ALLOWED_DOCUMENT_EXTENSIONS, IMAGE_ATTACHMENTS_TYPES } from '@ubeya/shared/constants';
import {
  ImagesCarousel,
  Flex,
  FileUpload as BaseFileUpload,
  flexCenter,
  FlexColumn,
  small,
  PdfModal
} from '@ubeya/shared-web/components';
import * as api from '@ubeya/shared/services/api';
import { ReactComponent as UploadImageIcon } from '@ubeya/shared-web/assets/compliance-upload-image.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import AttachmentActionsRenderer from './AttachmentActionsRenderer';

const Container = styled(Flex).attrs(() => ({ className: 'attachment-container' }))`
  flex-wrap: wrap;
`;

const FileUpload = styled(BaseFileUpload)`
  width: 400px;
  height: 400px;
  margin: 10px;
  border-radius: 3px;
  border: solid 1px ${({ theme }) => theme.colors.disabled};

  position: relative;
  cursor: ${({ enabled }) => (enabled ? 'pointer' : 'default')};
  ${flexCenter};
  ${small};

  background: ${({ theme }) => theme.colors.white};

  &&& {
    background-image: url(${({ image }) => image});
    background-size: cover;
    background-position: center;
  }

  color: ${({ theme }) => theme.colors.grey4};
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  text-align: center;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ComponentWrapper = styled.div`
  position: relative;

  .document-wrapper {
    margin: 10px 5px 0 0;
  }

  &:hover {
    .arrow {
      visibility: visible;
      background-color: ${({ theme }) => theme.colors.disabled};
      > path {
        fill: ${({ theme }) => theme.colors.white};
        stroke: none;
      }
    }
  }
`;

const InnerWrapper = styled(FlexColumn)`
  margin: 20px 0;
  align-items: center;
`;

const MainTitle = styled.div`
  font-size: 30px;
  margin: 30px 0 20px;
`;

const SubTitle = styled.div`
  font-size: 14px;
  width: 60%;
`;

const InnerImageUploadRenderer = ({ t, handleAddItems }) => (
  <FileUpload
    apiFunc={api.uploadImage}
    onUploaded={handleAddItems}
    accept={ALLOWED_DOCUMENT_EXTENSIONS}
    loaderSize="small">
    <InnerWrapper>
      <UploadImageIcon />
      <MainTitle>{t('addDocument')}</MainTitle>
      <SubTitle>{t('addDocumentDescription')}</SubTitle>
    </InnerWrapper>
  </FileUpload>
);

const EmployeeAttachment = ({ data, handleDeleteItems, handleAddItems }) => {
  const [isOpenIndex, setIsOpenIndex] = useState();
  const [openPdf, setOpenPdf] = useState();
  const { t } = useContext(ConfigContext);

  const mediaAttachments = useMemo(
    () => data?.filter(({ type }) => type === 'image').map(({ src }) => ({ source: src })),
    [data]
  );

  if (data?.length === 0) {
    return <InnerImageUploadRenderer t={t} handleAddItems={handleAddItems} />;
  }

  return (
    <>
      <Container>
        {(data || [])
          .filter(({ Component }) => Component !== undefined)
          .map(({ src, Component, name, title, extension, id }, index) => {
            const handlePreview = IMAGE_ATTACHMENTS_TYPES.includes(extension)
              ? () => setIsOpenIndex(index)
              : () => setOpenPdf(src);

            return (
              <ComponentWrapper key={src}>
                <Component
                  key={index}
                  onClick={() => setIsOpenIndex(index)}
                  title={title}
                  extension={extension}
                  src={src}
                  subTitle={name || extension}
                  preload="false"
                />

                <AttachmentActionsRenderer
                  onPreview={handlePreview}
                  onDelete={() => handleDeleteItems(id)}
                  showPreview={IMAGE_ATTACHMENTS_TYPES.includes(extension) || extension === 'pdf'}
                  extension={extension}
                  onDownload={() => window.open(src)}
                />
              </ComponentWrapper>
            );
          })}
      </Container>

      {!!openPdf && <PdfModal url={openPdf} onClose={() => setOpenPdf(undefined)} />}
      <ImagesCarousel views={mediaAttachments} currentIndex={isOpenIndex} onClose={() => setIsOpenIndex()} />
    </>
  );
};

export default EmployeeAttachment;
